import { MatchReportTable } from '@components/MatchReport/MatchReportTable';
import { UpdateMatchReport } from '@components/MatchReport/UpdateMatchReport';
import { StepContent } from '@components/StepElements';
import { faCircleX, faCloudExclamation, faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSalesBOMTimeoutHandler } from '@hooks/useSalesBOMTimeoutHandler';
import { Stack } from '@mui/material';
import { ReturnToProductLibraryButton } from '@pages/ManageProductPage/components/ReturnToProductLibraryButton';
import { OutlinedButton } from '@pages/UploadPage/styles';
import { WizardErrorPage } from '@partful/features/instructions/ui/WizardErrorPage';
import { useCancelSBOMJob } from '@partful/pages/create-product/sales-bom/hooks';
import styles from '@partful/pages/create-product/sales-bom/SalesBOM.module.scss';
import { ErrorList } from '@partful/pages/create-product/sales-bom/ui/ErrorList/ErrorList';
import { useInvalidateQueries } from '@partful/shared/lib/hooks/useInvalidateQueries';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { Button } from '@partful/shared/ui/Button';
import { WizardPageContent, WizardPageLayout } from '@partful/shared/ui/layouts/WizardPageLayout';
import { LoadingMessage } from '@partful/shared/ui/LoadingMessage/LoadingMessage';
import { useConfiguration } from '@providers/Configuration';
import type { SalesBomJob } from '@providers/job/types';
import { useTenant } from '@providers/Tenant';
import { GetConfigurationMatchReportResponse, productInventoryEndpoints } from '@services/productInventory';
import { useManageProductPageAnalytics } from '@services/useManageProductPageAnalytics';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const REFETCH_JOB_INTERVAL = 10000;

export function MatchReportUpdateSalesBOM() {
  const { accountId } = useTenant();

  const { t } = useTranslation('createProduct');

  const { name: productName } = useConfiguration();
  const { configurationPID } = useQueryParams('configurationPID');

  const [ignoreFailedJob, setIgnoreFailedJob] = useState<string | undefined>();
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const { invalidateContentProcessingJobsQueries, invalidateConfigurationsQuery } = useInvalidateQueries();

  const { sendSalesBOMUploadEvent, sendMatchReportMissingEvent } = useManageProductPageAnalytics();

  const { data: jobs, isLoading: isDeterminingInitialJobStatus } = useProductInventoryQuery<SalesBomJob[]>(
    productInventoryEndpoints.contentProcessing.root,
    {
      account: accountId,
      jobType: 'SalesBOM',
      configurationPID,
    }
  );

  // this is the source of truth for the job status, so if the page is refreshed, it will show the correct job status
  const salesBOMJobData = jobs?.[0];
  const salesBOMJobRunID = salesBOMJobData?.runID;

  // if there is an existing in progress job, then start polling this job - this is a local variable derived from the source of truth
  const inProgressSalesBOMJobRunID = salesBOMJobData?.status === 'IN_PROGRESS' ? salesBOMJobData?.runID : null;
  const { data: inProgressSalesBOMJobData } = useProductInventoryQuery<SalesBomJob>(
    `${productInventoryEndpoints.contentProcessing.sbom}/${inProgressSalesBOMJobRunID}`,
    { account: accountId },
    {
      enabled: Boolean(inProgressSalesBOMJobRunID),
      refetchInterval: REFETCH_JOB_INTERVAL,
      notifyOnChangeProps: 'all',
    }
  );

  const {
    data: configurationMatchReportData,
    remove: clearCachedConfigurationMatchReportData,
    isError: isErrorLoadingConfigurationMatchReport,
  } = useProductInventoryQuery<GetConfigurationMatchReportResponse>(
    `/v2${productInventoryEndpoints.configurations}/${configurationPID}/match-report`,
    { account: accountId, configurationPID },
    {
      enabled: !inProgressSalesBOMJobRunID,
    }
  );

  useEffect(() => {
    /* istanbul ignore next */
    if (isErrorLoadingConfigurationMatchReport) {
      sendMatchReportMissingEvent();
    }
  }, [isErrorLoadingConfigurationMatchReport, sendMatchReportMissingEvent]);

  const { cancelJob: cancelInProgressJob } = useCancelSBOMJob(accountId, salesBOMJobData?.runID);

  // periodically check if this in-progress jobs has timed out so the page isn't waiting forever
  useSalesBOMTimeoutHandler(inProgressSalesBOMJobData, accountId);

  useEffect(() => {
    /* istanbul ignore next - this will be better tested as an e2e test */
    if (['FAILED', 'COMPLETED'].includes(inProgressSalesBOMJobData?.status ?? '')) {
      // invalidate the queries first so the page doesn't show the loading screen and shows the correct job status
      invalidateConfigurationsQuery().then(() => {
        invalidateContentProcessingJobsQueries().then(() => {
          if (inProgressSalesBOMJobData?.status === 'COMPLETED') {
            // show the success message only if the inprogress job completed - if the job completed in the background (no inProgressSalesBOMJobData) then the complete message will not show (this is ok)
            setIsComplete(true);
          }
        });
      });
    }
  }, [inProgressSalesBOMJobData, invalidateContentProcessingJobsQueries, invalidateConfigurationsQuery]);

  const cancelJob = async () => {
    await cancelInProgressJob();
    await invalidateContentProcessingJobsQueries();
  };

  const retryUpload = () => {
    setIgnoreFailedJob(salesBOMJobData?.runID);
  };

  const onSalesBOMUploadedSuccessfully = async () => {
    sendSalesBOMUploadEvent();

    await invalidateContentProcessingJobsQueries();
  };

  const isInProgress = salesBOMJobData?.status === 'IN_PROGRESS' || inProgressSalesBOMJobData?.status === 'IN_PROGRESS';

  if (isDeterminingInitialJobStatus) {
    return null;
  }

  if (
    !isComplete && // if the in-progress job completed, then don't show the progress screen
    isInProgress
  ) {
    clearCachedConfigurationMatchReportData();

    return (
      <WizardPageLayout>
        <WizardPageContent>
          <Stack sx={{ alignItems: 'center' }}>
            <LoadingMessage
              title={t('sbom.form.processing.processingSBOMStatus')}
              message={t('freeToLeaveThisPage')}
              actions={
                <OutlinedButton
                  color="error"
                  endIcon={<FontAwesomeIcon icon={faCircleX} />}
                  onClick={() => cancelJob()}
                >
                  {t('common:cancel')}
                </OutlinedButton>
              }
            />
          </Stack>
        </WizardPageContent>
      </WizardPageLayout>
    );
  }

  if (salesBOMJobData?.status === 'FAILED' && ignoreFailedJob !== salesBOMJobData?.runID) {
    const errorTitle = (
      <>
        <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('sbom.form.failure.errorProcessingSBOM')}{' '}
      </>
    );
    return (
      <WizardErrorPage
        errorIcon={<FontAwesomeIcon icon={faCloudExclamation} />}
        errorTitle={errorTitle}
        errorMessage={<ErrorList runID={salesBOMJobData.runID} salesBOMJobData={salesBOMJobData} />}
        errorActions={
          <Button className={styles.uploadButton} onClick={() => retryUpload()}>
            {t('sbom.form.failure.uploadNewFile')}
          </Button>
        }
      />
    );
  }

  // wait for configurationMatchReportData to be loaded before rendering the page
  if (!configurationMatchReportData && !isErrorLoadingConfigurationMatchReport) {
    return null;
  }

  return (
    <>
      <StepContent>
        <Stack
          data-testid="match-report-container"
          direction={{ xs: 'column', md: 'row' }}
          spacing={7}
          useFlexGap
          sx={{
            '& > *': {
              flex: { xs: '1 1 100%', md: '1 1 50%' }, // Full width on mobile, 50% on desktop
              maxWidth: { xs: '100%', md: '50%' }, // Ensure max width is 50% on desktop
            },
          }}
        >
          {isErrorLoadingConfigurationMatchReport ? (
            /* istanbul ignore next - this is a temp message while MR gets re-generated */
            <div>Unable to fetch existing match report</div>
          ) : (
            <MatchReportTable productName={productName} data={configurationMatchReportData} />
          )}
          <UpdateMatchReport
            unsuccessfullyMatched={
              /* istanbul ignore else - this is a temp message while MR gets re-generated */
              configurationMatchReportData?.unsuccessfullyMatched ??
              /* istanbul ignore next - this is a temp message while MR gets re-generated */ 0
            }
            configurationPID={configurationPID}
            salesBOMJobRunID={salesBOMJobRunID!}
            onSalesBOMUploadedSuccessfully={onSalesBOMUploadedSuccessfully}
            isComplete={isComplete}
            onSalesBOMUpload={() => setIsComplete(false)}
          />
        </Stack>
      </StepContent>
      <ReturnToProductLibraryButton />
    </>
  );
}
